import { useState, useEffect, useRef } from 'react';
import { setBatchExpectedCount } from '../services/barcodeBatch';
import { extractDeliveryReceipt } from '../services/mlService';
import { getPremiumStatus } from '../services/serverDataService';
import {
  loadCategories,
  createCategory,
  uploadDocument,
  downscaleImageFile,
} from '../services/documentService';
import { deleteFromDrive } from '../services/googleDriveService';
import { isTokenValid, refreshGoogleAccessToken, setGoogleAccessToken } from '../services/googleAuthToken';
import { requestDriveToken } from '../services/googleAuthService';
import { tGlobal } from '../utils/tGlobal';

const RECEIPT_STORAGE_KEY = 'packageCounterReceiptCodes';
const PREMIUM_BACKDOOR_EMAILS = ['wiesn2022@gmail.com', 'philipp.rank1987@googlemail.com'];

function normalizeReceiptTracking(raw = '') {
  return String(raw || '')
    .trim()
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();
}

function trackingMatchesReceiptCode(savedCodeRaw, receiptCodeRaw) {
  const a = normalizeReceiptTracking(savedCodeRaw);
  const b = normalizeReceiptTracking(receiptCodeRaw);
  if (!a || !b || a.length < 6 || b.length < 6) return false;
  if (a === b) return true;
  if (a.length >= 8 && b.length >= 8 && (a.endsWith(b.slice(-10)) || b.endsWith(a.slice(-10)))) {
    return true;
  }
  if (a.includes(b) || b.includes(a)) return true;
  return false;
}

async function checkIsPremiumAccount() {
  try {
    const token = localStorage.getItem('kiosk_jwt_access');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload?.email && PREMIUM_BACKDOOR_EMAILS.includes(String(payload.email).toLowerCase())) {
        return true;
      }
    }
    const status = await getPremiumStatus();
    if (status?.isPremium) return true;
    if (status?.trialEndsAt && new Date(status.trialEndsAt) > new Date()) return true;
  } catch { /* ignore */ }
  return false;
}

async function ensureDriveConnected(interactive = false) {
  if (isTokenValid()) return true;
  try {
    const refreshed = await refreshGoogleAccessToken();
    if (refreshed || isTokenValid()) return true;
  } catch { /* ignore */ }
  try {
    const silentToken = await requestDriveToken(false);
    if (silentToken) {
      setGoogleAccessToken(silentToken);
      return true;
    }
  } catch { /* ignore */ }
  if (interactive) {
    try {
      const interactiveToken = await requestDriveToken(true);
      if (interactiveToken) {
        setGoogleAccessToken(interactiveToken);
        return true;
      }
    } catch { /* ignore */ }
  }
  return isTokenValid();
}

async function mergeReceiptFilesToPdfOrSingle(files) {
  const validFiles = (Array.isArray(files) ? files : []).filter(Boolean);
  if (validFiles.length === 0) return null;
  if (validFiles.length === 1 && validFiles[0].type === 'application/pdf') {
    return validFiles[0];
  }
  const imageFiles = validFiles.filter((f) => f.type?.startsWith('image/'));
  if (imageFiles.length === 0) {
    return validFiles[0];
  }
  try {
    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF({ unit: 'px', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    for (let i = 0; i < imageFiles.length; i++) {
      if (i > 0) pdf.addPage();
      const small = await downscaleImageFile(imageFiles[i], 1800, 0.86);
      let dims = { w: 1200, h: 1600 };
      let bitmap;
      try {
        bitmap = await createImageBitmap(small);
        dims = { w: bitmap.width, h: bitmap.height };
      } catch { /* fallback */ } finally {
        if (bitmap && typeof bitmap.close === 'function') bitmap.close();
      }
      const margin = 16;
      const maxW = pageW - 2 * margin;
      const maxH = pageH - 2 * margin;
      const scale = Math.min(maxW / dims.w, maxH / dims.h, 1);
      const imgW = dims.w * scale;
      const imgH = dims.h * scale;
      const x = (pageW - imgW) / 2;
      const y = (pageH - imgH) / 2;
      const format = small.type === 'image/png' ? 'PNG' : 'JPEG';
      const bytes = new Uint8Array(await small.arrayBuffer());
      pdf.addImage(bytes, format, x, y, imgW, imgH);
    }
    const pdfBlob = pdf.output('blob');
    return new File([pdfBlob], 'einlieferungsbeleg.pdf', { type: 'application/pdf' });
  } catch (err) {
    console.warn('📄 PDF-Merge für Einlieferungsbeleg fehlgeschlagen, speichere Einzelbild:', err);
    return imageFiles[0];
  }
}

/**
 * Custom Hook für Package Counter System (inkl. Foto-Abgleich für Einlieferungsbeleg & Auto-Save in Docs!)
 */
function usePackageCounter(setInfoMessage) {
  const [packageCounter, setPackageCounter] = useState(0);
  const [showCounterInput, setShowCounterInput] = useState(false);
  const [counterInputValue, setCounterInputValue] = useState('');
  const [receiptCodes, setReceiptCodes] = useState(() => {
    try {
      const raw = localStorage.getItem(RECEIPT_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [receiptScanning, setReceiptScanning] = useState(false);
  const [showReceiptChecklist, setShowReceiptChecklist] = useState(false);
  const [missingReceiptSheets, setMissingReceiptSheets] = useState([]);

  // 📄 Automatische Ablage in Docs (nur für Premium-Nutzer)
  const [receiptDocsStatus, setReceiptDocsStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'needs_setup' | 'error'
  const [receiptDocsFileName, setReceiptDocsFileName] = useState('');
  const [receiptPages, setReceiptPages] = useState([]); // [{ id, file, previewUrl, status: 'scanning'|'done'|'error', codes: [], slips: [], totalCount: 0 }]
  const receiptFilesRef = useRef([]);
  const receiptCodesRef = useRef(receiptCodes);
  const receiptSlipsRef = useRef([]);
  const receiptSumTotalRef = useRef(0);
  const scanQueueRef = useRef(Promise.resolve());
  const activeScansCountRef = useRef(0);
  const uploadedDocIdRef = useRef(null);
  const lastCarrierRef = useRef('DHL');

  const persistReceiptCodes = (nextList) => {
    const clean = Array.isArray(nextList) ? nextList : [];
    receiptCodesRef.current = clean;
    setReceiptCodes(clean);
    try {
      if (clean.length > 0) {
        localStorage.setItem(RECEIPT_STORAGE_KEY, JSON.stringify(clean));
      } else {
        localStorage.removeItem(RECEIPT_STORAGE_KEY);
      }
    } catch { /* ignore */ }
  };

  /**
   * Speichert alle gesammelten Fotos/Seiten des aktuellen Einlieferungsbelegs als PDF
   * direkt in Docs (Google Drive) unter der Kategorie „DHL Einlieferungsbeleg" (wird bei Bedarf automatisch angelegt).
   * Nur für Premium-Konten aktiv!
   */
  const saveCurrentReceiptToDocs = async (codesList = receiptCodes, interactiveSetup = false) => {
    const pageFiles = receiptFilesRef.current || [];
    if (!pageFiles.length) return;

    const isPrem = await checkIsPremiumAccount();
    if (!isPrem) {
      setReceiptDocsStatus('idle');
      return;
    }

    setReceiptDocsStatus('saving');
    const connected = await ensureDriveConnected(interactiveSetup);
    if (!connected) {
      setReceiptDocsStatus('needs_setup');
      return;
    }

    try {
      // 1. Kategorien prüfen & ggf. „DHL Einlieferungsbeleg" automatisch anlegen
      let cats = [];
      try {
        cats = await loadCategories();
      } catch {
        cats = [];
      }
      let targetCat = cats.find(
        (c) =>
          c.id === 'dhl-einlieferungsbeleg' ||
          c.id === 'einlieferungsbeleg' ||
          /einlieferungsbeleg/i.test(c.name || '')
      );
      if (!targetCat) {
        try {
          targetCat = await createCategory({
            id: 'dhl-einlieferungsbeleg',
            name: 'DHL Einlieferungsbeleg',
            icon: '📋',
            order: 5,
          });
        } catch {
          targetCat = { id: 'dhl-einlieferungsbeleg', name: 'DHL Einlieferungsbeleg', icon: '📋' };
        }
      }

      // 2. Alle bisherigen Seiten dieses Belegs zu einer PDF zusammenführen
      const mergedFile = await mergeReceiptFilesToPdfOrSingle(pageFiles);
      if (!mergedFile) {
        setReceiptDocsStatus('idle');
        return;
      }

      // 3. Falls beim 1. Foto schon ein Entwurf hochgeladen wurde und jetzt Seite 2 (+Beleg) dazukam → alten Entwurf ersetzen
      if (uploadedDocIdRef.current) {
        try {
          await deleteFromDrive(uploadedDocIdRef.current);
        } catch { /* ignore */ }
        uploadedDocIdRef.current = null;
      }

      const todayStr = new Date().toISOString().split('T')[0];
      const allCodeStrings = (Array.isArray(codesList) ? codesList : []).map((c) => c.code || c).filter(Boolean);
      const doc = await uploadDocument(mergedFile, {
        categoryId: targetCat.id || 'dhl-einlieferungsbeleg',
        categoryName: targetCat.name || 'DHL Einlieferungsbeleg',
        supplier: lastCarrierRef.current || '',
        date: todayStr,
        notes: `${allCodeStrings.length} Sendungen (${pageFiles.length} ${pageFiles.length === 1 ? 'Seite' : 'Seiten'})`,
        ocrText: allCodeStrings.join(' '),
      });

      uploadedDocIdRef.current = doc?.id || null;
      setReceiptDocsFileName(doc?.fileName || doc?.name || 'DHL-Einlieferungsbeleg.pdf');
      setReceiptDocsStatus('saved');
      if (window.showToast) {
        window.showToast(
          `📄 Einlieferungsbeleg automatisch in Docs gespeichert (${doc?.fileName || 'PDF'})`,
          'success',
          4000
        );
      }
    } catch (err) {
      console.warn('📄 Auto-Save Einlieferungsbeleg in Docs fehlgeschlagen:', err);
      if (err?.message?.includes('Authentifizierung') || err?.code === 'NO_DRIVE_CONNECTION' || err?.code === 'DRIVE_REAUTH_NEEDED') {
        setReceiptDocsStatus('needs_setup');
      } else {
        setReceiptDocsStatus('error');
      }
    }
  };

  const connectAndSaveReceiptToDocs = async () => {
    await saveCurrentReceiptToDocs(receiptCodesRef.current, true);
  };

  const computeMissingSheets = (slipsList = []) => {
    const sheetTotals = {};
    for (const s of slipsList) {
      const m = String(s?.sheet || '').match(/Blatt\s*(\d+)\s*von\s*(\d+)/i);
      if (m) {
        const cur = parseInt(m[1], 10);
        const tot = parseInt(m[2], 10);
        if (tot > 1) {
          if (!sheetTotals[tot]) sheetTotals[tot] = new Set();
          sheetTotals[tot].add(cur);
        }
      }
    }
    const missing = [];
    for (const [totStr, seenSet] of Object.entries(sheetTotals)) {
      const tot = parseInt(totStr, 10);
      for (let i = 1; i <= tot; i++) {
        if (!seenSet.has(i)) missing.push(`Blatt ${i} von ${tot}`);
      }
    }
    return missing;
  };

  /**
   * Initialisiert den Counter mit der erwarteten Paketanzahl
   * @param {number|string} totalPackages - Anzahl der erwarteten Pakete
   */
  const initializeCounter = (totalPackages, { keepReceipt = false } = {}) => {
    const count = parseInt(totalPackages, 10);
    if (count > 0) {
      setPackageCounter(count);
      localStorage.setItem('packageCounter', count.toString());
      if (!keepReceipt) {
        persistReceiptCodes([]);
        receiptFilesRef.current = [];
        receiptSlipsRef.current = [];
        receiptSumTotalRef.current = 0;
        setReceiptPages((prev) => {
          prev.forEach((p) => { if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl); });
          return [];
        });
        uploadedDocIdRef.current = null;
        setReceiptDocsStatus('idle');
        setReceiptDocsFileName('');
      }
      try { setBatchExpectedCount(count); } catch { /* */ }
      console.log(`📦 Counter initialisiert: ${count} Pakete erwartet`);

      if (setInfoMessage) {
        setInfoMessage(`📦 ${tGlobal('ui.counter.set', { count })}`);
        setTimeout(() => setInfoMessage(''), 3000);
      }
    }
  };

  /**
   * 📷 Fotografiert einen oder mehrere Einlieferungsbelege (Kamera-Foto + beliebig oft „+ Weiteres Foto hinzufügen"),
   * zeigt sofort Vorschaubilder der aufgenommenen Seiten an, setzt den Zähler automatisch,
   * gleicht beim Speichern jedes Pakets die Sendungsnummern ab UND speichert alle Seiten
   * bei Premium-Nutzern zusammen als mehrseitiges PDF unter „Docs" -> „DHL Einlieferungsbeleg"!
   */
  const scanDeliveryReceiptFiles = async (fileList, existingPakete = []) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    // Sofort Vorschaubilder für alle neu aufgenommenen Fotos anlegen, damit der Nutzer
    // direkt „📸 + Weiteres Foto hinzufügen" antippen kann, während Seite 1 im Hintergrund gelesen wird!
    const newPageEntries = files.map((file, idx) => ({
      id: `rcp-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`,
      file,
      previewUrl: file.type?.startsWith('image/') ? URL.createObjectURL(file) : null,
      status: 'scanning',
      codes: [],
      slips: [],
      totalCount: 0,
    }));

    receiptFilesRef.current = [...(receiptFilesRef.current || []), ...files];
    setReceiptPages((prev) => [...prev, ...newPageEntries]);
    setShowCounterInput(false);
    activeScansCountRef.current += newPageEntries.length;
    setReceiptScanning(true);

    const runJob = async () => {
      for (const pageEntry of newPageEntries) {
        try {
          const res = await extractDeliveryReceipt(pageEntry.file);
          if (res?.carrier) lastCarrierRef.current = res.carrier;
          const extractedCodes = Array.isArray(res?.codes) ? res.codes : [];
          const fileTotal = Number(res?.totalCount) || extractedCodes.length;
          const pageSlips = Array.isArray(res?.slips) ? res.slips : [];

          receiptSumTotalRef.current = (receiptSumTotalRef.current || 0) + fileTotal;
          receiptSlipsRef.current = [...(receiptSlipsRef.current || []), ...pageSlips];

          let mergedCodes = [...(receiptCodesRef.current || [])];
          let addedOnPage = 0;

          for (const rawCode of extractedCodes) {
            const norm = normalizeReceiptTracking(rawCode);
            if (norm.length < 6) continue;
            if (mergedCodes.some((item) => trackingMatchesReceiptCode(item.code, norm))) continue;

            const existingMatch = (Array.isArray(existingPakete) ? existingPakete : []).find(
              (p) => p && !p.deleted && trackingMatchesReceiptCode(p.trackingNumber || p.trackingNummer || '', norm)
            );

            mergedCodes.push({
              code: norm,
              matched: Boolean(existingMatch),
              matchedName: existingMatch?.empfaenger || existingMatch?.name || '',
              matchedId: existingMatch?.id || null,
              isInventoryReturn: Boolean(existingMatch && !existingMatch.ausgegeben && !existingMatch.zurueckgeschickt),
              matchedAt: existingMatch ? new Date().toISOString() : null,
              pageId: pageEntry.id,
            });
            addedOnPage += 1;
          }

          setReceiptPages((prev) =>
            prev.map((p) =>
              p.id === pageEntry.id
                ? {
                    ...p,
                    status: 'done',
                    codesCount: extractedCodes.length || addedOnPage,
                    totalCount: fileTotal,
                    slips: pageSlips,
                  }
                : p
            )
          );

          const missing = computeMissingSheets(receiptSlipsRef.current);
          setMissingReceiptSheets(missing);
          persistReceiptCodes(mergedCodes);

          const unmatchedCount = mergedCodes.filter((c) => !c.matched).length;
          const effectiveTotal = Math.max(mergedCodes.length, receiptSumTotalRef.current || 0);
          const matchedCount = mergedCodes.filter((c) => c.matched).length;
          const remaining = Math.max(unmatchedCount, effectiveTotal - matchedCount);

          if (remaining > 0) {
            setPackageCounter(remaining);
            localStorage.setItem('packageCounter', String(remaining));
            try { setBatchExpectedCount(effectiveTotal); } catch { /* */ }
          }

          const msg = mergedCodes.length > 0
            ? `📋 Einlieferungsbeleg erkannt: ${effectiveTotal} Pakete (${mergedCodes.length} Sendungsnr. zum Auto-Abgleich${matchedCount > 0 ? `, ${matchedCount} schon im Bestand` : ''})`
            : (effectiveTotal > 0
                ? `📋 Einlieferungsbeleg erkannt: ${effectiveTotal} Pakete`
                : '⚠️ Keine Sendungsnummern auf diesem Foto erkannt — bitte näher fotografieren');

          if (window.showToast) {
            window.showToast(msg, effectiveTotal > 0 ? 'success' : 'warning', 4000);
          } else if (setInfoMessage) {
            setInfoMessage(msg);
            setTimeout(() => setInfoMessage(''), 4000);
          }
        } catch (err) {
          setReceiptPages((prev) =>
            prev.map((p) => (p.id === pageEntry.id ? { ...p, status: 'error' } : p))
          );
          const errMsg = `⚠️ Einlieferungsbeleg konnte nicht gelesen werden: ${err?.message || 'Fehler'}`;
          if (window.showToast) window.showToast(errMsg, 'warning', 4000);
          else if (setInfoMessage) setInfoMessage(errMsg);
        } finally {
          activeScansCountRef.current = Math.max(0, activeScansCountRef.current - 1);
          if (activeScansCountRef.current === 0) {
            setReceiptScanning(false);
            // 📄 Sobald alle fotografierten Seiten ausgewertet sind → gemeinsam als 1 PDF in Docs speichern!
            saveCurrentReceiptToDocs(receiptCodesRef.current, false);
          }
        }
      }
    };

    scanQueueRef.current = scanQueueRef.current.then(runJob, runJob);
    return scanQueueRef.current;
  };

  /**
   * Entfernt eine einzelne fotografierte Seite wieder aus dem Einlieferungsbeleg
   */
  const removeReceiptPage = (pageId) => {
    setReceiptPages((prev) => {
      const target = prev.find((p) => p.id === pageId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      const nextPages = prev.filter((p) => p.id !== pageId);
      receiptFilesRef.current = nextPages.map((p) => p.file).filter(Boolean);
      receiptSlipsRef.current = nextPages.flatMap((p) => p.slips || []);
      receiptSumTotalRef.current = nextPages.reduce((sum, p) => sum + (Number(p.totalCount) || 0), 0);
      setMissingReceiptSheets(computeMissingSheets(receiptSlipsRef.current));

      const nextCodes = (receiptCodesRef.current || []).filter((c) => c.pageId !== pageId);
      persistReceiptCodes(nextCodes);
      const unmatched = nextCodes.filter((c) => !c.matched).length;
      setPackageCounter(unmatched);
      if (unmatched > 0) {
        localStorage.setItem('packageCounter', String(unmatched));
      } else {
        localStorage.removeItem('packageCounter');
      }
      if (nextPages.length > 0 && activeScansCountRef.current === 0) {
        saveCurrentReceiptToDocs(nextCodes, false);
      }
      return nextPages;
    });
  };

  /**
   * Reduziert den Counter um 1 (wird nach erfolgreicher Paket-Erfassung aufgerufen)
   * und hakt — falls ein Einlieferungsbeleg fotografiert wurde — die passende Sendungsnummer ab!
   */
  const decrementCounter = (savedPaket = null) => {
    let matchedEntry = null;
    let updatedReceipt = receiptCodes;

    const savedTracking = String(savedPaket?.trackingNumber || savedPaket?.trackingNummer || '').trim();
    if (savedTracking && receiptCodes.length > 0) {
      let foundIdx = receiptCodes.findIndex(
        (item) => !item.matched && trackingMatchesReceiptCode(savedTracking, item.code)
      );
      if (foundIdx < 0) {
        foundIdx = receiptCodes.findIndex((item) => trackingMatchesReceiptCode(savedTracking, item.code));
      }
      if (foundIdx >= 0) {
        matchedEntry = receiptCodes[foundIdx];
        updatedReceipt = receiptCodes.map((item, idx) =>
          idx === foundIdx
            ? {
                ...item,
                matched: true,
                matchedName: savedPaket?.name || item.matchedName || '',
                matchedAt: new Date().toISOString(),
              }
            : item
        );
        persistReceiptCodes(updatedReceipt);
      }
    }

    if (packageCounter > 0) {
      const newCount = packageCounter - 1;
      setPackageCounter(newCount);
      localStorage.setItem('packageCounter', newCount.toString());
      console.log(`📦 Counter reduziert: ${newCount} Pakete verbleibend`);

      if (matchedEntry && window.showToast) {
        const openCodes = updatedReceipt.filter((c) => !c.matched).length;
        window.showToast(
          `✅ Vom Einlieferungsbeleg abgehakt: …${matchedEntry.code.slice(-6)} (${openCodes} auf Beleg noch offen)`,
          'success',
          3200
        );
      } else if (!matchedEntry && savedTracking && receiptCodes.length > 0 && window.showToast) {
        window.showToast(
          `ℹ️ Sendung …${savedTracking.slice(-6)} stand nicht auf dem fotografierten Einlieferungsbeleg`,
          'info',
          3500
        );
      }

      if (newCount === 0 && setInfoMessage) {
        setInfoMessage(`🎉 ${tGlobal('ui.counter.allDone')}`);
        setTimeout(() => setInfoMessage(''), 5000);
      }
    } else if (matchedEntry && window.showToast) {
      const openCodes = updatedReceipt.filter((c) => !c.matched).length;
      window.showToast(
        `✅ Vom Einlieferungsbeleg abgehakt: …${matchedEntry.code.slice(-6)} (${openCodes} noch offen)`,
        'success',
        3200
      );
    }
  };

  /**
   * Setzt den Counter (und den Einlieferungsbeleg) zurück
   */
  const resetCounter = () => {
    setPackageCounter(0);
    setShowReceiptChecklist(false);
    setMissingReceiptSheets([]);
    receiptFilesRef.current = [];
    receiptSlipsRef.current = [];
    receiptSumTotalRef.current = 0;
    setReceiptPages((prev) => {
      prev.forEach((p) => { if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl); });
      return [];
    });
    uploadedDocIdRef.current = null;
    setReceiptDocsStatus('idle');
    setReceiptDocsFileName('');
    persistReceiptCodes([]);
    localStorage.removeItem('packageCounter');
    console.log('📦 Counter zurückgesetzt');

    if (setInfoMessage) {
      setInfoMessage(`📦 ${tGlobal('ui.counter.reset')}`);
      setTimeout(() => setInfoMessage(''), 3000);
    }
  };

  /**
   * Lädt den Counter aus localStorage (beim App-Start)
   */
  const loadCounter = () => {
    try {
      const saved = localStorage.getItem('packageCounter');
      if (saved) {
        const count = parseInt(saved, 10);
        if (count > 0) {
          setPackageCounter(count);
          console.log(`📦 Counter geladen: ${count} Pakete verbleibend`);
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden des Counters:', error);
    }
  };

  /**
   * Toggle für Counter-Input Anzeige
   */
  const toggleCounterInput = () => {
    setShowCounterInput(!showCounterInput);
    if (!showCounterInput) {
      setCounterInputValue('');
    }
  };

  /**
   * Handler für Counter-Input-Änderungen
   */
  const handleCounterInputChange = (value) => {
    if (/^\d*$/.test(value)) {
      setCounterInputValue(value);
    }
  };

  /**
   * Bestätigt und setzt den eingegebenen Counter-Wert
   */
  const confirmCounterInput = () => {
    if (counterInputValue && parseInt(counterInputValue, 10) > 0) {
      initializeCounter(counterInputValue);
      setShowCounterInput(false);
      setCounterInputValue('');
    }
  };

  /**
   * Berechnet die Farbe basierend auf dem Counter-Status
   * @returns {string} CSS-Farbe (grün, gelb, rot)
   */
  const getCounterColor = () => {
    if (packageCounter === 0) return '#666';
    if (packageCounter <= 3) return '#ff4444';
    if (packageCounter <= 10) return '#ffaa00';
    return '#4CAF50';
  };

  useEffect(() => {
    loadCounter();
  }, []);

  return {
    packageCounter,
    showCounterInput,
    counterInputValue,
    receiptCodes,
    receiptPages,
    removeReceiptPage,
    receiptScanning,
    showReceiptChecklist,
    setShowReceiptChecklist,
    missingReceiptSheets,
    receiptDocsStatus,
    receiptDocsFileName,
    connectAndSaveReceiptToDocs,
    scanDeliveryReceiptFiles,
    initializeCounter,
    decrementCounter,
    resetCounter,
    toggleCounterInput,
    handleCounterInputChange,
    confirmCounterInput,
    getCounterColor,
  };
}

export default usePackageCounter;
